import React, { useEffect } from "react";
import { CalendarDays, ChevronRight, Globe2, ShieldCheck, Sparkles, Wifi } from "lucide-react";
import "./EsimPlans.scss";
import { Button } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { post } from "../../../utils/request";
import { getContext, getTabId } from "../../../utils/context";
import { AppError } from "../../../utils/customError";

// const plans = Array(9).fill({
//   countries: 43,
//   data: "40 GB",
//   validity: "29 days",
//   price: "$100",
// });

const EsimPlans = ({
  plans,
  selected,
  setSelected,
  onClickCoverage,
  showErrorInfo,
  locationType,
  location
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const destinationLabel = plans?.[0]?.displayCoverage || location || "your destination";
  const selectedCurrency = getContext().currency || "USD";

  const formatPlanPrice = (plan) => {
    const currency = plan?.currencyCode || selectedCurrency || "USD";
    const rawDisplayPrice = String(plan?.displayPrice || "").replace(/^from\s+/i, "").trim();
    const priceValue = plan?.price;

    if (rawDisplayPrice) {
      const includesCurrencyCode = /\b[A-Z]{3}\b/.test(rawDisplayPrice);
      return includesCurrencyCode ? rawDisplayPrice : `${currency} ${rawDisplayPrice}`;
    }

    if (Number.isFinite(Number(priceValue))) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
        }).format(Number(priceValue));
      } catch (error) {
        return `${currency} ${Number(priceValue).toFixed(2)}`;
      }
    }

    return currency;
  };

  const handleNext = async (selectedPackage) => {
  
    const tabId = getTabId();
    try {
      const esimPreorderResult = await post("/esim/preorder", selectedPackage, {'X-Ctx-Tab-Id': tabId});
      navigate("/esim/payment-step", { state: { selectedPackage, esimPreorderResult, locationType, location } });
    } catch (err) {
      if (err instanceof AppError && err.code === 20001) {
        showErrorInfo(err.message);
        return;
      }

      toast.error('Something went wrong, pleate try again.');
    }
  };

  useEffect(() => {
    let referralCode = searchParams.get("code");
    if (!referralCode) {
      return;
    }

    let data = searchParams.get("data");
    let validity = searchParams.get("validity");
    if (!data & !validity) {
      return;
    }

    let volumeInGB = parseInt(data);
    let duration = parseInt(validity);
    
    let selectedPlan = plans.filter(p => p.volumeInGB === volumeInGB && p.duration === duration)?.[0] || null;
    if (!selectedPlan) {
      return;
    }

    setSelected(plans.indexOf(selectedPlan));
    handleNext({...selectedPlan, referralCode});
  }, [plans, searchParams]);

  return (
    <section className="esim-plans-section">
      <div className="esim-plans-wrapper">
        <div className="esim-results-hero">
          <div>
            <p className="esim-results-eyebrow">Travel eSIM plans</p>
            <h1>Stay connected in {destinationLabel}</h1>
            <p>
              Compare instant data plans by coverage, data size, validity, and price.
              Pick a plan now and keep your setup ready before takeoff.
            </p>
          </div>
          <div className="esim-results-summary" aria-label="eSIM search summary">
            <span>{plans?.length || 0}</span>
            <small>plans found</small>
          </div>
        </div>

        <div className="esim-results-toolbar">
          <div>
            <h2>Available plans</h2>
            <p>Found {plans?.length || 0} results for your search</p>
          </div>
          <div className="esim-results-trust">
            <span><Sparkles size={15} /> Instant delivery</span>
            <span><ShieldCheck size={15} /> Secure checkout</span>
          </div>
        </div>

        {/* <h3 className="section-heading">
          Get an eSim data plan for{" "}
          {selectedArea?.value !== "" ? selectedArea?.name : selectedRegion}
        </h3> */}

        {/* Plans */}
        <div className="plans-container">
          {plans?.length ? (
          <div className="grid">
            {plans?.map((plan, i) => (
              <div
                key={i}
                className={`plan-card ${selected === i ? "selected" : ""}`}
              >
                <div className="details">
                  <div className="plan-card__top">
                    <div>
                      <div className="plan-card__label">Travel eSIM</div>
                      <div className="title">{plan.planName}</div>
                    </div>
                    <div className="price">
                      {formatPlanPrice(plan)}
                    </div>
                  </div>

                  <div className="plan-card__metrics">
                    <div>
                      <Wifi size={17} />
                      <span>Data</span>
                      <strong>{plan.displayVolume}</strong>
                    </div>
                    <div>
                      <CalendarDays size={17} />
                      <span>Validity</span>
                      <strong>{plan.displayValidity}</strong>
                    </div>
                  </div>

                  <div className="coverage-row">
                    <div className="coverage-row__label">
                      <Globe2 size={17} />
                      <span>Coverage</span>
                    </div>
                    {plan.coverage > 1 ? (
                      <button
                        type="button"
                        className="coverage-btn"
                        onClick={() => onClickCoverage(plan)}
                      >
                      <span>{plan.displayCoverage}</span>
                      <ChevronRight size={14} />
                    </button>
                    ) : (
                      <div className="coverage-value">{plan.displayCoverage}</div>
                    )}
                  </div>

                  <Button className="buy-btn" onClick={() => {setSelected(i);handleNext(plan);}}>
                    Buy eSIM
                    <ChevronRight size={16} />
                  </Button>
                </div>

                
              </div>
            ))}
          </div>
          ) : (
            <div className="esim-no-results">
              <Globe2 size={28} />
              <h3>No eSIM plans found</h3>
              <p>Try another destination or coverage type from the search above.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EsimPlans;
